import {originalChannels} from "@hosttools/core/constant";
import buildChannels from "@hosttools/core/shared/buildChannels";
import {type FC, memo, useCallback, useMemo} from "react";
import {useHistory} from "react-router-dom";

import type {AccountType} from "../../models/account";
import type {Listing} from "../../models/listing";
import {useChannelData} from "../../react";
import type {Props as ChannelLogoProps} from "../ChannelLogo";
import ChannelLogo from "../ChannelLogo";
import {HStack} from "../ui/hstack";

type Props = {
    listing: Listing;
    showStatus?: boolean;
    showDirectBooking?: boolean;
    onPress?: (channel: AccountType | (string & {})) => void;
    onRemove?: (channel: AccountType | (string & {})) => void;
} & Pick<ChannelLogoProps, "size" | "status">;

const ListingChannelsLogo: FC<Props> = ({
    listing,
    size,
    showStatus,
    showDirectBooking,
    onPress,
    onRemove
}) => {
    const channels = useMemo(() => buildChannels(listing), [listing]);
    const {directBookingIDs} =
        useChannelData(showDirectBooking ? listing._id : undefined, "direct") ?? {};
    const history = useHistory();

    const goToDirectBooking = useCallback(() => {
        if (onPress) {
            onPress("direct");
        } else {
            // default will navigate to the DB page
            history.push(`/settings/direct-bookings?id=${listing._id}`);
        }
    }, [history, listing._id, onPress]);

    if (channels.length || directBookingIDs?.length) {
        return (
            <HStack className="items-center" space="sm" testID="channels-logo">
                {channels.map(channel => {
                    if (originalChannels.includes(channel) || channel === "direct") {
                        return (
                            <ChannelLogo
                                key={channel}
                                channel={channel}
                                size={size}
                                onPress={channel === "direct" ? goToDirectBooking : onPress}
                                onRemove={onRemove}
                            />
                        );
                    }
                    const channelObj = listing.channels[channel];

                    return channelObj.channels?.map(channelItem => {
                        return (
                            <ChannelLogo
                                key={channelItem.channel}
                                isChannelCC
                                channel={channelItem.channel}
                                logoUrl={channelItem.logo}
                                size={size}
                                status={showStatus ? channelItem.status : undefined}
                                onPress={onPress}
                                onRemove={channelItem.status === "listed" ? onRemove : undefined}
                            />
                        );
                    });
                })}
            </HStack>
        );
    }
    return null;
};

export default memo(ListingChannelsLogo);
